import { useMemo } from 'react';
import { ApprovalStatus, ApprovalStepStatus, ApprovalUserType } from 'ui-product-approvals-common';
import { isReassignSupportedForType } from '../utils/isReassignSupportedForType';
import { useApprovalContext } from './useApprovalContext';
import { useHasApprovalScope } from './useHasApprovalScope';
export const useApprovalConditions = () => {
  var _approval$meta;
  const approval = useApprovalContext();
  const hasApproveScope = useHasApprovalScope(approval === null || approval === void 0 || (_approval$meta = approval.meta) === null || _approval$meta === void 0 ? void 0 : _approval$meta.approvalType, 'approve');
  return useMemo(() => {
    const {
      config,
      meta,
      data
    } = approval;
    const {
      approvalStatus,
      isLoading,
      isSuperAdmin,
      currentUserType,
      stepStatusForCurrentUser,
      userCanSkipThisApproval,
      restartOnConclude,
      approvalType
    } = meta;
    const noApprovalData = data === null;

    // config.enabled can be null when config fetch has not finished. So these are just explicit checks
    const isEnabled = config.enabled === true;
    const isDisabled = config.enabled === false;
    const isNotStarted = noApprovalData || approvalStatus === ApprovalStatus.CANCELLED;
    const isConcluded = data && data.approvalConcluded;
    const isApproved = approvalStatus === ApprovalStatus.APPROVED;
    const isConcludedAndShouldRestartOnConclude = restartOnConclude && isConcluded;
    const isChangesRequested = approvalStatus === ApprovalStatus.CHANGES_REQUESTED;
    const isApprovalActive = approvalStatus === ApprovalStatus.APPROVAL_PENDING || approvalStatus === ApprovalStatus.CHANGES_REQUESTED;
    const isUserStepPending = stepStatusForCurrentUser === ApprovalStepStatus.APPROVAL_PENDING;
    const isUserStepApproved = stepStatusForCurrentUser === ApprovalStepStatus.APPROVED;
    const isUserStepChangesRequested = stepStatusForCurrentUser === ApprovalStepStatus.CHANGES_REQUESTED;

    // This is used as a source of truth of whether approvals is allowing render
    // of high risk action button for any reason (enabled, skip or restarting approvals).
    const canUserTakeApprovableAction = !isEnabled || userCanSkipThisApproval || isApproved && !isConcludedAndShouldRestartOnConclude;
    const canEditApproval = isApprovalActive && (currentUserType === ApprovalUserType.REQUESTER || hasApproveScope || isSuperAdmin);
    const canReassignApprovers = canEditApproval && isReassignSupportedForType(approvalType);
    const hasObjectIds = Boolean(meta.approvableObjectId) || (meta.approvalObjectIds || []).length > 0;
    // Once we know enabled is false, we consider the fetch flow complete
    // We consider waiting for approvable object id as part of the fetch
    const isFetchComplete = config.enabled === false || hasObjectIds && config.enabled !== null && !meta.isLoading;
    const isAwaitingApprovableObject = isEnabled && !hasObjectIds;
    return {
      canEditApproval,
      canReassignApprovers,
      canUserTakeApprovableAction,
      isApprovalActive,
      isApproved,
      isApprover: currentUserType === ApprovalUserType.APPROVER,
      isAwaitingApprovableObject,
      isCancelled: approvalStatus === ApprovalStatus.CANCELLED,
      isChangesRequested,
      isConcluded,
      isConcludedAndShouldRestartOnConclude,
      isDisabled,
      isEnabled,
      isFetchComplete,
      isLoading,
      isNonParticipant: currentUserType === ApprovalUserType.NON_PARTICIPANT,
      isNotStarted,
      isPending: approvalStatus === ApprovalStatus.APPROVAL_PENDING,
      isRejected: approvalStatus === ApprovalStatus.REJECTED,
      isRequester: currentUserType === ApprovalUserType.REQUESTER,
      isSuperAdmin,
      isUserStepApproved,
      isUserStepChangesRequested,
      isUserStepPending,
      noApprovalData,
      userCanSkipThisApproval
    };
  }, [approval, hasApproveScope]);
};